/** Shared on-disk / in-memory format for tracked points. Version 1. */

export const TRACK_FORMAT_VERSION = 1 as const;

export type QualityPreset = "fast" | "balanced" | "dense";

export interface TrackPoint {
  /** Pixel x in the tracked frame (origin top-left). */
  u: number;
  /** Pixel y in the tracked frame. */
  v: number;
  /** Camera-space X (pinhole unprojection). */
  x: number;
  /** Camera-space Y (OpenGL-style, +Y up). */
  y: number;
  /** Camera-space Z (toward the camera is smaller Z in our convention: scene +Z toward viewer). */
  z: number;
  /** 1 if the tracker still saw this point at this frame. */
  vis: number;
}

export interface Track {
  id: number;
  /** 0 = static-ish, 1 = clearly moving relative to the median flow. */
  dynamic: number;
  /** RGB in 0–1, sampled at birth. */
  color: [number, number, number];
  /** One sample per frame in the dataset. */
  points: TrackPoint[];
}

export interface TrackDataset {
  version: typeof TRACK_FORMAT_VERSION;
  backend: string;
  width: number;
  height: number;
  frameCount: number;
  fps: number;
  durationSec: number;
  /** Assumed pinhole used to lift 2D→3D. */
  camera: { fx: number; fy: number; cx: number; cy: number };
  tracks: Track[];
}

export interface TrackOptions {
  quality: QualityPreset;
  maxWidth: number;
  targetFps: number;
  maxFrames: number;
  maxPoints: number;
  winSize: number;
  levels: number;
  minDistance: number;
  windowLength: number;
  voxelPx: number;
}

export const QUALITY_PRESETS: Record<QualityPreset, Omit<TrackOptions, "quality">> = {
  fast: {
    maxWidth: 320,
    targetFps: 8,
    maxFrames: 32,
    maxPoints: 280,
    winSize: 9,
    levels: 2,
    minDistance: 8,
    windowLength: 12,
    voxelPx: 6,
  },
  balanced: {
    maxWidth: 480,
    targetFps: 12,
    maxFrames: 48,
    maxPoints: 560,
    winSize: 13,
    levels: 3,
    minDistance: 7,
    windowLength: 16,
    voxelPx: 5,
  },
  dense: {
    maxWidth: 640,
    targetFps: 15,
    maxFrames: 64,
    maxPoints: 900,
    winSize: 15,
    levels: 4,
    minDistance: 6,
    windowLength: 16,
    voxelPx: 4,
  },
};

export function optionsFor(quality: QualityPreset): TrackOptions {
  return { quality, ...QUALITY_PRESETS[quality] };
}

export function emptyDataset(): TrackDataset {
  return {
    version: TRACK_FORMAT_VERSION,
    backend: "none",
    width: 0,
    height: 0,
    frameCount: 0,
    fps: 0,
    durationSec: 0,
    camera: { fx: 1, fy: 1, cx: 0, cy: 0 },
    tracks: [],
  };
}
